import { put, select, call, all, takeLatest } from "redux-saga/effects";
import sortBy from "lodash/sortBy";
import { doRotationsGetRotationSetups } from "./rotations";
import InstitutionsApi from "../api/Institutions";
import StorageApi from "../api/Storage";
import FileCategoriesApi from "../api/FileCategories";
import TermsApi from "../api/Terms";
import { populateFileCategories } from "../utils/populateFileCategories";
import {
  Auth,
  FileCategories,
  Institutions,
  Rotations,
  Terms,
} from "../actions/";
// Type
import type { Action } from "deox";
import type { tAPI } from "../api/API";
import type { UndefinedNull } from "../../types/common.types";
import type { StateRotations } from "../../types/rotations.types";
import type { RootState } from "../reducers";
import type { StateTerms } from "../reducers/terms";
import type { State as StateInstitutions } from "../reducers/institutions";
import type { CalendarsConfigProps } from "../../components/shared/availabilities-days/AvailabilitiesDaysTabProps.types";
//Sagas for institutions
function* doInstitutionsFetchInstitutions(
  action: Action<
    string,
    {
      status?: string;
      name?: string;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsFetchInstitutionsRequest());
  window.logger.log("doInstitutionsFetchInstitutions", action);
  const {
    status = "",
    name = "",
    callbackSuccess = null,
    callbackError = null,
  } = action.payload || {};
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get all institutions for this company
    const { data } = yield call(
      InstitutionsApi.GetInstitutions,
      api,
      status,
      name,
      { token }
    );
    const { institutions } = data;
    const institutionsSortByName = sortBy(institutions, ["name"]);
    yield put(
      Institutions.institutionsFetchInstitutionsSuccess({
        institutions: institutionsSortByName,
      })
    );
    if (callbackSuccess) {
      callbackSuccess(institutionsSortByName);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsFetchInstitutions",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsFetchInstitutionsFailure());
    if (callbackError) {
      callbackError(error);
    }
  }
}

function* doInstitutionsCreate(
  action: Action<
    string,
    {
      body: object;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsCreateRequest());
  window.logger.log("doInstitutionsCreate", action);
  const { body, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to create new institution
    const { data } = yield call(InstitutionsApi.CreateInstitution, api, body, {
      token,
    });
    const { institution } = data;
    yield put(Institutions.institutionsCreateSuccess({ institution }));
    if (callbackSuccess) {
      callbackSuccess(institution);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error("ErrorSaga - doInstitutionsCreate", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsCreateFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

const getRolesInstitutionCustomFieldsRequiredNull = ({ customFields }: any) => {
  if (customFields.length) {
    const customFieldsRequired = customFields.filter(
      ({ isRequired }: any) => isRequired
    );
    for (let i = 0; i < customFieldsRequired.length; i++) {
      const { permissions, value } = customFieldsRequired[i];
      const rolesWrite = Object.keys(permissions).filter(
        (role) => permissions[role] === "write"
      );
      for (let j = 0; j < rolesWrite.length; j++) {
        const roleWrite = rolesWrite[j];
        if (
          permissions[roleWrite] === "write" &&
          (value === null || value === undefined)
        ) {
          return rolesWrite;
        }
      }
    }
  }
  return [];
};

export function* doMergeInstitutionAccount(
  institution: Object,
  isEdit: boolean
): Generator<any, void, any> {
  const { tenantSettings }: { tenantSettings: any } = yield select(
    ({ tenantSettings }) => tenantSettings
  );
  const hasToCheckRequiredFields =
    tenantSettings["ACCOUNTS_INSTITUTION_REQUIRED_FIELDS"].valuesApi;
  const hasInstitutionCustomFieldsRequiredNull = hasToCheckRequiredFields
    ? getRolesInstitutionCustomFieldsRequiredNull(institution)
    : [];
  window.logger.log(
    "doMergeInstitutionAccount",
    institution,
    isEdit,
    "hasInstitutionCustomFieldsRequiredNull: ",
    hasInstitutionCustomFieldsRequiredNull
  );
  if (!isEdit) {
    yield put(
      Institutions.institutionsFetchInstitutionSuccess({
        institution,
        hasInstitutionCustomFieldsRequiredNull:
          hasInstitutionCustomFieldsRequiredNull.length
            ? {
                institutionRequired: institution,
                rolesWrite: hasInstitutionCustomFieldsRequiredNull,
                isRequired: true,
              }
            : null,
      })
    );
  } else {
    yield put(
      Institutions.institutionsEditInstitutionSuccess({
        institution,
        hasInstitutionCustomFieldsRequiredNull:
          hasInstitutionCustomFieldsRequiredNull
            ? {
                institutionRequired: institution,
                rolesWrite: hasInstitutionCustomFieldsRequiredNull,
                isRequired: true,
              }
            : null,
      })
    );
  }
}

function* doInstitutionsFetchInstitution(
  action: Action<
    string,
    {
      institutionId: string;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsFetchInstitutionRequest());
  window.logger.log("doInstitutionsFetchInstitution", action);
  const {
    institutionId,
    callbackSuccess = null,
    callbackError = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get a institution for that institutionId
    const { data } = yield call(
      InstitutionsApi.GetInstitution,
      api,
      institutionId,
      { token }
    );
    const { institution } = data;
    yield call(doMergeInstitutionAccount, institution, false);
    if (callbackSuccess) {
      callbackSuccess(institution);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsFetchInstitution",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsFetchInstitutionFailure());
    if (callbackError) {
      callbackError(error);
    }
  }
}

function* doInstitutionsEditInstitution(
  action: Action<
    string,
    {
      actionApi: "ACTIVATE_DEACTIVATE_INSTITUTION" | "EDIT_INSTITUTION";
      institutionId: string;
      body: object;
      isPostFile?: boolean;
      isMyInstitution?: boolean;
      callbackSuccess?: () => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsEditInstitutionRequest());
  window.logger.log("doInstitutionsEditInstitution", action);
  const {
    actionApi = "EDIT_INSTITUTION",
    institutionId,
    body,
    isPostFile: isUpload = false,
    isMyInstitution = false,
    callbackSuccess = null,
    callbackError = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    let patchBody = body;
    // If uploading a logo, do S3 upload first, then PATCH with fileId
    if (isUpload && actionApi === "EDIT_INSTITUTION") {
      const { fileId } = yield call(
        StorageApi.UploadFile,
        api,
        body as unknown as File,
        {
          belongsTo: "Institutions" as const,
          ownerId: institutionId,
          location: "profile" as const,
        },
        { token }
      );
      patchBody = { logo: fileId };
    }
    // call the api to update the institution profile
    const { data } = yield call(
      InstitutionsApi.EditInstitution,
      api,
      actionApi,
      institutionId,
      patchBody,
      false,
      { token }
    );
    const { institution } = data;
    if (isMyInstitution) {
      yield put(
        Auth.authFetchMyInstitution({ institutionId: institution._id })
      );
    } else {
      yield call(doMergeInstitutionAccount, institution, true);
    }
    if (callbackSuccess) {
      callbackSuccess();
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsEditInstitution",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsEditInstitutionFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doInstitutionsDelete(
  action: Action<
    string,
    {
      institutionId: string;
      callbackSuccess?: () => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsDeleteRequest());
  window.logger.log("doInstitutionsDelete", action);
  const {
    institutionId,
    callbackSuccess = null,
    callbackError = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to delete a institution for that specific institutionId
    yield call(InstitutionsApi.DeleteInstitution, api, institutionId, {
      token,
    });
    yield put(Institutions.institutionsDeleteSuccess({ institutionId }));
    if (callbackSuccess) {
      callbackSuccess();
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.log("ErrorSaga - doInstitutionsDelete", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsDeleteFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doInstitutionsFetchInstitutionAvailabilities(
  action: Action<
    "INSTITUTIONS/FETCH/INSTITUTION_AVAILABILITIES",
    {
      institutionId: string;
      institutionType: string | UndefinedNull;
      cbs?: (data: any) => void;
      cbe?: (error: any) => void;
    }
  >
) {
  window.logger.log("doInstitutionsFetchInstitutionAvailabilities", action);
  yield put(Institutions.institutionsFetchInstitutionAvailabilitiesRequest());
  const {
    institutionId,
    institutionType,
    cbs = null,
    cbe = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // TERMS
    const { terms: termsStore, lastFetching: lastFetchingTerms }: StateTerms =
      yield select(({ terms }: RootState) => terms);
    let terms = [...termsStore];
    const canFetchTerms =
      !lastFetchingTerms &&
      new Date().getTime() > (lastFetchingTerms || 0) + 60 * 60 * 1000;
    if (canFetchTerms) {
      const {
        data: { terms: termsAPI = [] },
      } = yield call(TermsApi.GetTerms, api, { token });
      yield put(Terms.termsFetchAllSuccess({ terms: termsAPI }));
      terms = [...termsAPI];
    }
    // ROTATIONS SETTINGS
    // Wrapped in try/catch: /rotation-setups API was removed from backend.
    // If fetch fails, availabilities still load normally with empty calendarsConfig.
    let rotationSetups: any[] = [];
    try {
      const {
        automaticSetups,
        lastFetchingRotationsSettings,
      }: StateRotations["rotationsSettings"] = yield select(
        ({ rotations: { rotationsSettings } }: RootState) => rotationsSettings
      );
      rotationSetups = [...automaticSetups];
      const canFetchRotationsSettings =
        !lastFetchingRotationsSettings &&
        new Date().getTime() >
          (lastFetchingRotationsSettings || 0) + 60 * 60 * 1000;
      if (canFetchRotationsSettings) {
        // call the api to fetch a list of setups for automatic rotations
        const { Students, automaticSetups: automaticSetupsApi } =
          yield doRotationsGetRotationSetups(action);
        yield put(
          Rotations.rotationsFetchSetupsAutomaticRotationsSuccess({
            automaticSetups: automaticSetupsApi,
            Students,
          })
        );
        rotationSetups = [...automaticSetupsApi];
      }
    } catch (rotationSetupsError) {
      window.logger.warn(
        "doInstitutionsFetchInstitutionAvailabilities - rotation setups not available, continuing without calendar config",
        rotationSetupsError
      );
      rotationSetups = [];
    }
    // INSTITUTION AVAILABILITIES
    let availabilities = [];
    const {
      institution: institutionStore,
      institutionId: institutionIdStore,
    }: {
      institution: StateInstitutions["institution"];
      institutionId: StateInstitutions["institutionId"];
    } = yield select(
      ({ institutions: { institution, institutionId } }: RootState) => ({
        institution,
        institutionId,
      })
    );
    if (
      institutionIdStore === institutionId &&
      institutionStore?._id === institutionId &&
      institutionStore?.availabilities?.length > 0
    ) {
      availabilities = institutionStore.availabilities;
    } else {
      // call the api to get a institution availabilities from specific institutionId
      const {
        data: { availabilities: availabilitiesAPI = [] },
      } = yield call(
        InstitutionsApi.GetInstitutionAvailabilities,
        api,
        institutionId,
        { token }
      );
      availabilities = [...availabilitiesAPI];
    }
    yield put(
      Institutions.institutionsFetchInstitutionAvailabilitiesSuccess({
        availabilities,
        institutionId,
      })
    );
    // SET CALENDAR CONFIGURATION
    const calendarsConfig: CalendarsConfigProps[] = rotationSetups
      .filter(({ institutions }) =>
        institutions.map(({ _id }) => _id).includes(institutionId)
      )
      .map((rotationSetup) => {
        const {
          _id: rotationSetupId,
          status,
          name,
          term: rotationSetupTerm,
          calendarType,
          institutionSettings,
        } = rotationSetup;
        const [term] = terms.filter(({ _id }) => _id === rotationSetupTerm);
        return {
          rotationSetup: rotationSetupId,
          status,
          name,
          term,
          calendarType: "unique",
          calendarSettings: institutionSettings
            .filter(
              (institutionSetting) =>
                institutionSetting.institutiontype === institutionType
            )
            .map((institutionSetting) => ({
              calendarSelectionType:
                calendarType === "clinicType"
                  ? institutionSetting.dateType
                  : "day",
              institutionType: institutionSetting.institutiontype,
              blocks: institutionSetting.blocks || [],
            })),
        };
      });
    if (cbs) {
      cbs({ availabilities, calendarsConfig });
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsFetchInstitutionAvailabilities",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsFetchInstitutionAvailabilitiesFailure());
    const { message } = errors || "";
    if (cbe) {
      cbe(message);
    }
  }
}

function* doInstitutionsCreateInstitutionAvailabilities(
  action: Action<
    "INSTITUTIONS/CREATE/INSTITUTION_AVAILABILITIES",
    {
      body: any;
      cbs?: (data: any) => void;
      cbe?: (error: any) => void;
    }
  >
) {
  window.logger.log("doInstitutionsCreateInstitutionAvailabilities", action);
  yield put(Institutions.institutionsCreateInstitutionAvailabilitiesRequest());
  const { body, cbs = null, cbe = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to create a new institution availabilities from specific institutionId
    const {
      data: { availability },
    } = yield call(InstitutionsApi.CreateInstitutionAvailabilities, api, body, {
      token,
    });
    yield put(
      Institutions.institutionsCreateInstitutionAvailabilitiesSuccess({
        availability,
      })
    );
    if (cbs) {
      cbs(availability);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsCreateInstitutionAvailabilities",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(
      Institutions.institutionsCreateInstitutionAvailabilitiesFailure()
    );
    const { message } = errors || "";
    if (cbe) {
      cbe(message);
    }
  }
}

function* doInstitutionsEditInstitutionAvailabilities(
  action: Action<
    "INSTITUTIONS/EDIT/INSTITUTION_AVAILABILITIES",
    {
      availabilityId: string;
      body: any;
      cbs?: (data: any) => void;
      cbe?: (error: any) => void;
    }
  >
) {
  window.logger.log("doInstitutionsEditInstitutionAvailabilities", action);
  yield put(Institutions.institutionsEditInstitutionAvailabilitiesRequest());
  const { availabilityId, body, cbs = null, cbe = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to edit a institution availabilities from specific institutionId
    const {
      data: { availability },
    } = yield call(
      InstitutionsApi.EditInstitutionAvailabilities,
      api,
      availabilityId,
      body,
      { token }
    );
    yield put(
      Institutions.institutionsEditInstitutionAvailabilitiesSuccess({
        availability,
      })
    );
    if (cbs) {
      cbs(availability);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsEditInstitutionAvailabilities",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsEditInstitutionAvailabilitiesFailure());
    const { message } = errors || "";
    if (cbe) {
      cbe(message);
    }
  }
}

function* doInstitutionsFetchInstitutionFiles(
  action: Action<
    string,
    {
      id: string;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsFetchInstitutionFilesRequest());
  window.logger.log("doInstitutionsFetchInstitutionFiles", action);
  const { id, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get a institution for that institutionId
    const { data } = yield call(InstitutionsApi.GetInstitutionFiles, api, id, {
      token,
    });
    const { files } = data;
    // Populate category: v3 returns category as string ID
    let fc: any[] = yield select(
      ({ fileCategories: { fileCategories } }) => fileCategories
    );
    if (!fc || fc.length === 0) {
      const { data: fcData } = yield call(
        FileCategoriesApi.GetFileCategories,
        api,
        { token }
      );
      fc = fcData.categories;
      yield put(
        FileCategories.fileCategoriesFetchFileCategoriesSuccess({
          fileCategories: fc,
        })
      );
    }
    const populatedFiles = populateFileCategories(files, fc);
    yield put(
      Institutions.institutionsFetchInstitutionFilesSuccess({
        files: populatedFiles,
      })
    );
    if (callbackSuccess) {
      callbackSuccess(populatedFiles);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsFetchInstitutionFiles",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsFetchInstitutionFilesFailure());
    if (callbackError) {
      callbackError(error);
    }
  }
}

function* doInstitutionsFetchInstitutionFilesExpired(
  action: Action<
    string,
    {
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsFetchInstitutionFilesExpiredRequest());
  window.logger.log("doInstitutionsFetchInstitutionFilesExpired", action);
  const { callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    const { data } = yield call(
      InstitutionsApi.GetInstitutionFilesExpired,
      api,
      { token }
    );
    const { files: filesExpired } = data;
    // Populate category: v3 returns category as string ID
    let fc: any[] = yield select(
      ({ fileCategories: { fileCategories } }) => fileCategories
    );
    if (!fc || fc.length === 0) {
      const { data: fcData } = yield call(
        FileCategoriesApi.GetFileCategories,
        api,
        { token }
      );
      fc = fcData.categories;
      yield put(
        FileCategories.fileCategoriesFetchFileCategoriesSuccess({
          fileCategories: fc,
        })
      );
    }
    const populatedFilesExpired = populateFileCategories(filesExpired, fc);
    yield put(
      Institutions.institutionsFetchInstitutionFilesExpiredSuccess({
        filesExpired: populatedFilesExpired,
      })
    );
    if (callbackSuccess) {
      callbackSuccess(populatedFilesExpired);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsFetchInstitutionFilesExpired",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsFetchInstitutionFilesExpiredFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doInstitutionsUploadInstitutionFiles(
  action: Action<
    string,
    {
      body: {
        file: File;
        belongsTo: string;
        ownerId: string;
        location: string;
        metadata: { [k: string]: any };
      };
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Institutions.institutionsUploadInstitutionFilesRequest());
  window.logger.log("doInstitutionsUploadInstitutionFiles", action);
  const { body, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // Upload file to S3 via 3-step flow
    const { fileId, file: confirmedFile } = yield call(
      StorageApi.UploadFile,
      api,
      body.file,
      {
        belongsTo: body.belongsTo as any,
        ownerId: body.ownerId,
        location: body.location as any,
      },
      { token }
    );
    // Set file metadata if provided
    if (body.metadata) {
      yield call(StorageApi.EditFile, api, fileId, body.metadata, { token });
    }
    yield put(Institutions.institutionsUploadInstitutionFilesSuccess());
    if (callbackSuccess) {
      callbackSuccess([confirmedFile]);
    }
  } catch (error: any) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doInstitutionsUploadInstitutionFiles",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Institutions.institutionsUploadInstitutionFilesFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

export default function* institutionsSagas(): Generator<any, any, any> {
  yield all([
    takeLatest(
      Institutions.INSTITUTIONS_FETCH_INSTITUTIONS,
      doInstitutionsFetchInstitutions
    ),
    takeLatest(Institutions.INSTITUTIONS_CREATE, doInstitutionsCreate),
    takeLatest(
      Institutions.INSTITUTIONS_FETCH_INSTITUTION,
      doInstitutionsFetchInstitution
    ),
    takeLatest(
      Institutions.INSTITUTIONS_EDIT_INSTITUTION,
      doInstitutionsEditInstitution
    ),
    takeLatest(Institutions.INSTITUTIONS_DELETE, doInstitutionsDelete),
    takeLatest(
      Institutions.INSTITUTIONS_FETCH_INSTITUTION_AVAILABILITIES,
      doInstitutionsFetchInstitutionAvailabilities
    ),
    takeLatest(
      Institutions.INSTITUTIONS_CREATE_INSTITUTION_AVAILABILITIES,
      doInstitutionsCreateInstitutionAvailabilities
    ),
    takeLatest(
      Institutions.INSTITUTIONS_EDIT_INSTITUTION_AVAILABILITIES,
      doInstitutionsEditInstitutionAvailabilities
    ),
    takeLatest(
      Institutions.INSTITUTIONS_FETCH_INSTITUTION_FILES,
      doInstitutionsFetchInstitutionFiles
    ),
    takeLatest(
      Institutions.INSTITUTIONS_FETCH_INSTITUTION_FILES_EXPIRED,
      doInstitutionsFetchInstitutionFilesExpired
    ),
    takeLatest(
      Institutions.INSTITUTIONS_UPLOAD_INSTITUTION_FILES,
      doInstitutionsUploadInstitutionFiles
    ),
  ]);
}
